import React from 'react';
import { Shield, Trophy, Users, Calendar, CheckCircle2, Flame } from 'lucide-react';

interface MobileDeviceMockupProps {
  mediaSrc?: string;
  mediaType?: 'image' | 'video';
  poster?: string;
}

export const MobileDeviceMockup: React.FC<MobileDeviceMockupProps> = ({
  mediaSrc,
  mediaType,
  poster
}) => {
  return (
    <div className="relative mx-auto w-full max-w-[270px] aspect-[9/19.5] rounded-[44px] p-2.5 bg-gradient-to-b from-[#1F293D] via-[#111624] to-[#0D111A] shadow-2xl shadow-cyan-500/10 border border-gray-700/50 select-none group">
      {/* Outer Glow Ring on Hover */}
      <div className="absolute -inset-1 rounded-[46px] bg-gradient-to-b from-[#00F0FF]/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500 -z-10 blur-sm pointer-events-none" />

      {/* Glass & Inner Screen Border */}
      <div className="relative w-full h-full rounded-[34px] bg-[#07090E] overflow-hidden border border-gray-800/80 flex flex-col justify-between text-left">
        
        {/* If custom media is supplied, render image or video */}
        {mediaSrc ? (
          mediaType === 'video' ? (
            <video
              src={mediaSrc}
              poster={poster}
              autoPlay
              loop
              muted
              playsInline
              className="w-full h-full object-cover"
            />
          ) : (
            <img
              src={mediaSrc}
              alt="App Screen Preview"
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover"
            />
          )
        ) : (
          /* High-Fidelity Cyber-Minimalist Mobile Screen Placeholder */
          <>
            {/* Top Status Bar & Dynamic Island */}
            <div className="relative pt-3 px-6 pb-2 flex items-center justify-between text-[10px] text-gray-400 font-mono z-20">
              <span>09:41</span>
              {/* Dynamic Island Pill */}
              <div className="w-20 h-4.5 bg-black rounded-full border border-gray-800 flex items-center justify-center space-x-1">
                <div className="w-1.5 h-1.5 rounded-full bg-gray-900" />
                <div className="w-2 h-2 rounded-full bg-[#00F0FF]/60 animate-pulse" />
              </div>
              <div className="flex items-center space-x-1">
                <span>5G</span>
                <div className="w-4 h-2 border border-gray-400 rounded-xs p-0.5 flex items-center">
                  <div className="w-full h-full bg-gray-400 rounded-2xs" />
                </div>
              </div>
            </div>

            {/* Screen Inner Content */}
            <div className="px-4 py-2 space-y-3 flex-1 overflow-hidden font-sans">
              
              {/* In-App Header */}
              <div className="flex items-center justify-between pt-1">
                <div className="flex items-center space-x-2">
                  <div className="w-7 h-7 rounded-full bg-[#00F0FF]/10 border border-[#00F0FF]/30 flex items-center justify-center text-[#00F0FF]">
                    <Shield size={13} />
                  </div>
                  <div>
                    <div className="text-[10px] text-gray-400 font-mono tracking-wider">EXECUTIVE JJ</div>
                    <div className="text-xs font-bold text-white leading-none">Luan Lima</div>
                  </div>
                </div>
                <div className="px-2 py-0.5 rounded-full bg-emerald-500/10 border border-emerald-500/30 text-[9px] font-mono text-emerald-400 flex items-center space-x-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-ping" />
                  <span>ONLINE</span>
                </div>
              </div>

              {/* Focus Card */}
              <div className="relative rounded-xl p-3 bg-gradient-to-br from-[#0E1424] to-[#0A0D16] border border-gray-800/90 space-y-2 overflow-hidden">
                <div className="absolute top-0 right-0 w-24 h-24 bg-[#00F0FF]/10 rounded-full blur-2xl pointer-events-none" />
                <div className="flex items-center justify-between text-[9px] font-mono text-[#00F0FF]">
                  <span className="flex items-center space-x-1">
                    <Flame size={10} />
                    <span>FOCO DO TREINO</span>
                  </span>
                  <span className="text-gray-400">HOJE 19:30</span>
                </div>
                <div>
                  <div className="text-sm font-extrabold text-white">Passagem & Guarda De La Riva</div>
                  <p className="text-[10px] text-gray-400 font-light mt-0.5 line-clamp-1">
                    Conceito de alavanca e controle de postura no tatame.
                  </p>
                </div>
                {/* Belt Progression Bar */}
                <div className="space-y-1 pt-1">
                  <div className="flex justify-between text-[9px] font-mono text-gray-400">
                    <span>PROGRESSO DE GRAU</span>
                    <span className="text-[#00F0FF] font-bold">85%</span>
                  </div>
                  <div className="w-full h-1.5 bg-gray-900 rounded-full overflow-hidden border border-gray-800">
                    <div className="h-full bg-gradient-to-r from-[#00F0FF] to-cyan-400 rounded-full w-[85%]" />
                  </div>
                </div>
              </div>

              {/* Quick Metrics Grid */}
              <div className="grid grid-cols-2 gap-2">
                <div className="p-2.5 rounded-lg bg-[#0A0D15] border border-gray-800/80">
                  <div className="text-[9px] text-gray-400 font-mono flex items-center space-x-1">
                    <Calendar size={10} className="text-[#00F0FF]" />
                    <span>PRESENÇA</span>
                  </div>
                  <div className="text-base font-extrabold text-white mt-0.5">24 <span className="text-[10px] font-light text-gray-400">dias</span></div>
                </div>
                <div className="p-2.5 rounded-lg bg-[#0A0D15] border border-gray-800/80">
                  <div className="text-[9px] text-gray-400 font-mono flex items-center space-x-1">
                    <Trophy size={10} className="text-[#00F0FF]" />
                    <span>TÉCNICAS</span>
                  </div>
                  <div className="text-base font-extrabold text-white mt-0.5">78 <span className="text-[10px] font-light text-gray-400">itens</span></div>
                </div>
              </div>

              {/* Class Schedule Mini-Item */}
              <div className="flex items-center justify-between p-2 rounded-lg bg-[#0A0D15]/60 border border-gray-800/60 text-[10px]">
                <div className="flex items-center space-x-2">
                  <CheckCircle2 size={12} className="text-emerald-400" />
                  <span className="text-gray-300 font-medium">Check-in Realizado</span>
                </div>
                <span className="text-gray-500 font-mono text-[9px]">Sincronizado Offline</span>
              </div>

            </div>

            {/* In-App Bottom Navigation */}
            <div className="px-5 py-2.5 bg-[#080B12]/95 border-t border-gray-800/80 flex items-center justify-between text-gray-400 z-20">
              <div className="flex flex-col items-center text-[#00F0FF] space-y-0.5">
                <Shield size={13} />
                <span className="text-[8px] font-mono">Início</span>
              </div>
              <div className="flex flex-col items-center hover:text-white transition-colors space-y-0.5">
                <Calendar size={13} />
                <span className="text-[8px] font-mono">Aulas</span>
              </div>
              <div className="flex flex-col items-center hover:text-white transition-colors space-y-0.5">
                <Trophy size={13} />
                <span className="text-[8px] font-mono">Evolução</span>
              </div>
              <div className="flex flex-col items-center hover:text-white transition-colors space-y-0.5">
                <Users size={13} />
                <span className="text-[8px] font-mono">Perfil</span>
              </div>
            </div>

            {/* Home Indicator Bar */}
            <div className="pb-2 pt-1 flex justify-center z-20">
              <div className="w-24 h-1 bg-gray-700 rounded-full" />
            </div>
          </>
        )}
      </div>
    </div>
  );
};
